<template>
  <NoPermission v-if="!isEarlySettlementActive" />
  <div v-else class="early-settlement-container">
    <Banner />
    <div class="early-settlement-content-wrapper">
      <LeftContent
        :summary="summary"
        :availability="availability"
        :availability-loading="availabilityLoading"
        :is-request-created="isRequestCreated"
        @calculate="handleCalculate"
        @validation-error="handleValidationError"
      />
      <RightContent
        :isEmpty="!hasUserCalculated"
        :calculation="calculateResult"
        :calculationLoading="calculateLoading"
        :availability="availability"
        :availability-loading="availabilityLoading"
        :summary="summary"
        :hasValidationError="hasValidationError"
        :is-request-created="isRequestCreated"
        @requestCreated="handleRequestCreated"
      />
    </div>
    <History :externalRow="lastCreatedRequest" />
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useStore } from 'vuex';
import Banner from './partials/Banner.vue';
import LeftContent from './partials/LeftContent.vue';
import RightContent from './partials/RightContent.vue';
import History from './partials/History.vue';
import NoPermission from '@/components/common/NoPermission/NoPermission.vue';
import useEarlySettlementSummary from './services/summaryService';
import useEarlySettlementCalculate from './services/calculateService';
import useEarlySettlementAvailability from './services/availabilityService';

export default {
  name: "EarlySettlement",
  components: {
    Banner,
    LeftContent,
    RightContent,
    History,
    NoPermission,
  },
  setup() {
    const store = useStore();
    const isEarlySettlementActive = computed(
      () => store.getters["common/getIsEarlySettlementActive"]
    );

    const { data, isLoading, error, fetchData } = useEarlySettlementSummary();
    const {
      data: calculateResult,
      isLoading: calculateLoading,
      error: calculateError,
      calculate,
      reset: resetCalculate,
    } = useEarlySettlementCalculate();

    const {
      data: availabilityData,
      isLoading: availabilityLoading,
      error: availabilityError,
      fetchAvailability,
    } = useEarlySettlementAvailability();

    const hasUserCalculated = ref(false);
    const lastCreatedRequest = ref(null);
    const hasValidationError = ref(false);
    const isRequestCreated = ref(false);

    const handleValidationError = (hasError) => {
      hasValidationError.value = hasError;
    };

    const handleCalculate = async (amount) => {
      if (!amount) return;

      await calculate({
        requestedAmount: Number(amount),
      });

      hasUserCalculated.value = true;
    };

    const clearCalculation = () => {
      resetCalculate();
    };

    const handleRequestCreated = async (row) => {
      lastCreatedRequest.value = row;
      isRequestCreated.value = true;
      
      await Promise.all([
        fetchData(),
        fetchAvailability(),
      ]);
    };

    onMounted(() => {
      if (isEarlySettlementActive.value) {
        fetchData();
        fetchAvailability();
      }
    });

    return {
      isEarlySettlementActive,
      summary: data,
      summaryLoading: isLoading,
      summaryError: error,
      availability: availabilityData,
      availabilityLoading,
      availabilityError,
      calculateResult,
      calculateLoading,
      calculateError,
      hasUserCalculated,
      lastCreatedRequest,
      hasValidationError,
      isRequestCreated,
      handleCalculate,
      clearCalculation,
      handleRequestCreated,
      handleValidationError,
    };
  },
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/variables.scss';
@import '@/assets/styles/utilities.scss';

.early-settlement-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 16px;
  .early-settlement-content-wrapper {
    display: flex;
    width: 100%;
    gap: 24px;
    align-items: stretch;
  }
}
</style>
